import { Component, h, JSX, Prop, Host, Element, State, Listen } from '@stencil/core';
import { HighlightBoxClassText, RichtextWidth } from './rcm-richtext.types';
import { TrackingService } from '../../services/tracking';
import { TrackingObjectBuilder } from '../../services/tracking/tracking-object-builder';
import { StringService } from '../../services';

@Component({
  tag: 'rcm-richtext',
  styleUrl: 'rcm-richtext.scss',
  shadow: false,
})
export class RcmRichtext {
  /** Parent Type */
  @Prop() type: string;

  /** Richtext width */
  @Prop() width: RichtextWidth = RichtextWidth.LARGE;

  /** Richtext level (from 1 to 3) */
  @Prop() public level = 1;

  /** Richtext with no margin */
  @Prop() public noMargin = false;

  /** Richtext highlightBox */
  @Prop() highlightBox: HighlightBoxClassText;

  /** Text color */
  @Prop() color?: string;

  @State() isArticle: boolean = false;

  @Element() element: HTMLRcmRichtextElement;

  @Listen('resize', { target: 'window' })
  handleResize() {
    if (!this.highlightBox) return;
    const { left, right } = this.element.getBoundingClientRect();
    const { left: containerLeft, right: containerRight } = this.element.querySelector('rcm-container').getBoundingClientRect();
    const marginLeft = containerLeft - left;
    const marginRight = right - containerRight;
    this.element.style.setProperty('--highlightbox-left', `${marginLeft}px`);
    this.element.style.setProperty('--highlightbox-right', `${containerRight + marginRight}px`);
  }

  handleLinkTracking() {
    const anchorTags = this.element.querySelectorAll('a');
    anchorTags.forEach((anchorTag) => {
      // eslint-disable-next-line no-param-reassign
      anchorTag.href = StringService.normalizeLink(anchorTag.getAttribute('href'));
      anchorTag.addEventListener('click', () => {
        TrackingService.push(
          new TrackingObjectBuilder()
            .forLinkClick({
              clickText: anchorTag.innerText,
              clickUrl: anchorTag.href,
              parentComponent: 'rcm-richtext',
            })
            .build(),
        );
      });
    });
  }

  componentDidRender() {
    if (this.element.parentNode.nodeName.toLowerCase() === 'rcm-layout-article') {
      this.isArticle = true;
      return;
    }
    this.handleResize();
    this.handleLinkTracking();
  }

  public render(): JSX.Element {
    const level = Math.min(3, this.level);
    const richtextClasses = {
      richtext: true,
      [`richtext--${level}`]: true,
      'richtext--width--medium': this.width === RichtextWidth.MEDIUM,
      'margin-0': this.noMargin,
      'box-grey--article': this.isArticle,
      [this.highlightBox]: !!this.highlightBox,
    };

    return (
      <Host class={{ wrapper: !this.highlightBox, 'margin-0': this.noMargin, 'wrapper-highlight-box': !!this.highlightBox }}>
        <rcm-container fullWidth={this.width === RichtextWidth.FULL}>
          <rcm-column span={12}>
            <div class={richtextClasses} style={{ color: this.color ? this.color : '' }}>
              <slot />
            </div>
          </rcm-column>
        </rcm-container>
      </Host>
    );
  }
}
